import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Label,
  ReferenceDot,
} from "recharts";
import { connect } from "react-redux";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { Card, CardContent } from "@mui/material";
const defaultColors = [
  "#3E8397", "#3E8397", "#3E8397", "#3E8397", "#3E8397",
  "#3E8397", "#3E8397", "#3E8397", "#3E8397", "#3E8397",
  "#3E8397", "#3E8397", "#3E8397", "#3E8397", "#3E8397"
];

function ConditionsChart(props) {
  const [conditions, setConditions] = useState([]);
  const [conditionColors, setConditionColors] = useState({});
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [chartData, setChartData] = useState([]);
  
  useEffect(() => {
    if (props.icpc_consultation2 && props.icpc_consultation2.data) {
      const availableConditions = props.icpc_consultation2.data.map(item => item.condition);
      const selection = availableConditions.filter(item => [
          "Ischaemic Heart Disease",
          "Diabetes",
          "Stroke/cerebrovascular accident",
          "Transient Ischaemic Attack",
          "Asthma"
        ].includes(item)
      )
      const colors = {};
      availableConditions.forEach((condition, index) => {
        colors[condition] = defaultColors[index % defaultColors.length];
      });
      
      setConditions(availableConditions);
      setConditionColors(colors);
      setSelectedConditions(selection);
      
      const transformedData = transformApiDataToChartData(props.icpc_consultation2);
      setChartData(transformedData);
    }
  }, [props.icpc_consultation2]);
  
  const transformApiDataToChartData = (apiData) => {
    if (!apiData || !apiData.years || !apiData.data) return [];
    
    const years = apiData.years;
    const chartData = years.map(year => ({ year: year.toString() }));
    
    apiData.data.forEach(item => {
      const condition = item.condition;
      item.yearCounts.forEach((count, index) => {
        if (chartData[index]) {
          chartData[index][condition] = count;
        }
      });
    });
    
    return chartData;
  };

  const toggleCondition = (condition) => {
    setSelectedConditions((prev) => {
      const newConditions = prev.includes(condition) 
        ? prev.filter((c) => c !== condition) 
        : [...prev, condition];
      // Increment refresh key after state update
      setRefreshKey(key => key + 1);
      return newConditions;
    });
  };
  
  const CustomizedLabel = (props) => {
    const { viewBox, value, condition } = props;
    if (!viewBox || !value) return null;
    
    return (
      <text
        x={viewBox.x + viewBox.width + 10}
        y={viewBox.y + viewBox.height / 2}
        fill={conditionColors[condition]}
        fontSize="14"
        fontWeight="bold"
        textAnchor="start"
        dominantBaseline="middle"
      >
        {condition}
      </text>
    );
  };

  return (
    <div className="w-full flex flex-col items-center">
      {/* Button aligned to the right */}
      <div className="w-full flex justify-end mb-4 pr-4">
        <Button variant="contained" onClick={() => setShowChecklist(!showChecklist)}>
          Choose Conditions
        </Button>
      </div>

      {showChecklist && (
        <Card className="p-4 mb-4">
          <CardContent>
            {conditions.map((condition) => (
              <div key={condition} className="flex items-center mb-2">
                <Checkbox
                  checked={selectedConditions.includes(condition)}
                  onChange={() => toggleCondition(condition)}
                />
                <span className="ml-2">{condition}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ResponsiveContainer width="90%" height={400}>
        <LineChart key={`chart-${refreshKey}`}  data={chartData} margin={{ top: 10, right: 105, left: 20, bottom: 10 }}>
          <XAxis dataKey="year" />
          <YAxis />
          <Tooltip />
          {/* Removed the Legend component */}

          {/* Transparent grid lines */}
          <CartesianGrid stroke="none" borderColor="transparent" />

          {selectedConditions.map((condition) => (
            <Line
            key={`line-${condition}-${refreshKey}`}
              type="monotone"
              dataKey={condition}
              stroke={conditionColors[condition] || "gray"}
              strokeWidth={2}
              dot={false}
            />
          ))}

          {/* Labels at the end of each line */}
          {chartData.length > 0 && selectedConditions.map((condition) => {
            const lastDataPoint = chartData[chartData.length - 1];
            const value = lastDataPoint[condition];
            if (!value) return null;
            
            return (
              <ReferenceDot
                key={`dot-${condition}-${refreshKey}`}
                x={lastDataPoint.year}
                y={value}
                r={0}
                label={{
                  value: condition,
                  position: 'right',
                  offset: 10,
                  fill: conditionColors[condition],
                  fontSize: 14,
                  fontWeight: 'bold'
                }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

const mapState = state => ({
  icpc_consultation2: state.overview.icpc_consultation2
});

const mapDispatch = ({
  overview: { getPromise_6 }
}) => ({
  getPromise_6: () => getPromise_6()
});

export default connect(mapState, mapDispatch)(ConditionsChart);